import BadgeCheckSVG from "@/assets/svg/badge-check.svg";
import YoutubeIconSVG from "@/assets/svg/youtube-icon.svg";
import { EnfinitySecondaryBtn } from "@/components/design-system/enfinity/EnfinityButton";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import scrollTo from "@/utils/scrollTo";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

export default function Header() {
  const { ref, isVisible } = useScrollReveal();
  const [showRest, setShowRest] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowRest(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const restVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 45,
        damping: 8,
        mass: 1.2,
        duration: 2,
      },
    },
  };

  const badge = {
    name: "Official YouTube Certified",
    icon: YoutubeIconSVG,
    url: "https://servicesdirectory.withyoutube.com/directory/enfinity-talent-agency/",
  };

  return (
    <div
      id="header"
      ref={ref}
      className={`${
        isVisible ? "bounce-up" : ""
      } py-20 xxs:py-16 xxs:pt-24 xl:pt-28 1xl:pt-40  lg:py-16 xl:py-[15px] 1xl:py-[30px] 2xl:py-32 lg:pt-28 4xl:py-[115px] 4xl:pt-28 px-4 xxs:px-8 xs:px-10 sm:px-10 md:px-10 lg:px-12 xl:px-12 4xl:px-16 relative bg-[#1a1a1a]`}
    >
      {/* Background Image for Mobile */}
      <div
        className="absolute inset-0 w-full h-full bg-cover bg-no-repeat z-0 lg:hidden"
        style={{
          backgroundImage: `url('https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/shield-bg-mobile.png')`,
          backgroundPosition: "center bottom",
        }}
      />
      {/* Background Image for Desktop */}
      <div
        className="absolute inset-0 w-full h-full bg-cover bg-no-repeat z-0 hidden lg:block"
        style={{
          backgroundImage: `url('https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/shield-bg.png')`,
          backgroundPosition: "center bottom",
        }}
      />

      {/* Content Overlay */}
      <div className="relative z-10">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-[30px] relative">
          {/* Badge */}
          <div className="flex justify-center">
            <a href={badge.url} target="_blank" rel="noopener noreferrer">
              <div
                className="flex items-center gap-2 rounded-full py-1 px-4
             border border-white/20 bg-white/5 backdrop-blur-md text-white shimmer-badge"
              >
                <img src={badge.icon} alt={badge.name} className="h-8 w-8" />
                <span>{badge.name}</span>
                <img src={BadgeCheckSVG} className="h-5 w-5" alt="Badge" />
              </div>
            </a>
          </div>

          {/* Trusted by Section */}
          {/* <div className="flex flex-col lg:flex-row items-center justify-center gap-2 lg:gap-[10px] rounded-[100px] h-auto lg:h-[43px]">
          <div className="flex items-center gap-[10px]">
            {[...Array(5)].map((_, index) => (
              <Star
                key={index}
                className="w-6 h-6 text-yellow-400 fill-yellow-400"
                strokeWidth={1}
              />
            ))}
          </div>
          <p className="text-lg font-medium text-white text-center lg:text-left enfinity-subtext-font">
            <span className="lg:hidden">Trusted by 4,500+ creators</span>
            <span className="hidden lg:inline">Trusted by 4,500+ creators monetizing and protecting their content worldwide</span>
          </p>
        </div> */}

          {/* Main Heading */}
          <h1
            className="text-5xl xxs:text-[55px] lg:text-[80px] 4xl:text-[90px] font-normal text-white text-center enfinity-header-font"
            style={{ lineHeight: "1.2" }}
          >
            <span> Content protection</span>
            <br />
            <span className="relative inline-block">
              {/* Stroke layer with vertical gradient mask - behind fill */}
              <span
                className="absolute inset-0 text-white z-0"
                style={{
                  WebkitTextStroke: "2px white",
                  WebkitTextFillColor: "transparent",
                  maskImage:
                    "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
                  WebkitMaskImage:
                    "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
                  pointerEvents: "none",
                }}
              >
                built for creators
              </span>
              {/* Fill layer with original color - on top */}
              <span className="relative z-10 text-[#8b5cf6]">
                built for creators
              </span>
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl xxs:text-[1rem] lg:text-lg font-normal text-white text-center max-w-5xl leading-[30px] enfinity-subtext-font">
            Stop losing views to unauthorized reuploads. We protect your content
            24/7, identify stolen videos on <br /> YouTube, enforce claims
            automatically, and monetize eligible matches.
          </p>

          {/* Stats Container */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 py-10 px-8 rounded-[20px] w-full max-w-[760px]">
            <div className="flex flex-col gap-[5px] items-center">
              <p className="text-[62px] xxs:text-[55px] font-normal text-[#8b5cf6] leading-normal enfinity-header-font">
                $40M
              </p>
              <p className="text-lg xxs:text-[1rem] lg:text-lg 4xl:text-[18px] font-normal text-white enfinity-subtext-font">
                Revenue Recovered
              </p>
            </div>
            <div className="flex flex-col gap-[5px] items-center">
              <p className="text-[62px] xxs:text-[55px] font-normal text-[#8b5cf6] leading-normal enfinity-header-font">
                4.5B+
              </p>
              <p className="text-lg xxs:text-[1rem] 4xl:text-[18px] font-normal text-white enfinity-subtext-font">
                Views Matched & Claimed
              </p>
            </div>
            <div className="flex flex-col gap-[5px] items-center">
              <p className="text-[62px] xxs:text-[55px] font-normal text-[#8b5cf6] leading-normal enfinity-header-font">
                24/7
              </p>
              <p className="text-lg xxs:text-[1rem] font-normal text-white enfinity-subtext-font text-center">
                Automated Content <br />
                Protection
              </p>
            </div>
          </div>
        </div>

        {/* Button */}
        <motion.div
          variants={restVariants}
          initial="hidden"
          animate={showRest ? "visible" : "hidden"}
          className=""
        >
          <EnfinitySecondaryBtn
            onClick={() => scrollTo("how-much-shield")}
            name="Try the Revenue Calculator"
            className="absolute bottom-[-85px] lg:-bottom-[85px] xl:-bottom-[35px] 1xl:-bottom-[50px] 2xl:-bottom-[150px] 4xl:bottom-[-135px] left-1/2 -translate-x-1/2 shadow-[0_4px_6px_rgba(0,0,0,0.1)] z-10 text-white"
            style={{
              background: "linear-gradient(to right, #A78BFA, #8B5CF6)",
            }}
          />
        </motion.div>
      </div>
    </div>
  );
}
